import { defineStore } from 'pinia'
import { ref, Ref, computed } from 'vue'
import { instance, getApiPrefix, FileType } from '@cognitivecredit/ui-utils'
import { getApiDomain } from '@/config'
import {
  DealConnectV2Deal,
  DealConnectV2DealsResponse,
  DealConnectV2Document,
  DealConnectV2DocumentsResponse,
  DealConnectV2File,
} from '@/components/dealconnect/dealconnect-v2/types'

const getReq = async () => {
  const prefix = getApiPrefix('rac')
  return instance({ prefix, apiDomain: getApiDomain(prefix) })
}

const decodeDisplayPath = (filePath: string, source: string): string => {
  if (source !== 'managed_access') {
    return filePath
  }
  try {
    return decodeURIComponent(decodeURIComponent(filePath))
  } catch {
    return filePath
  }
}

const toPrivateDocumentLabel = (value: boolean | null): 'private' | 'public' | null => {
  if (value === true) {
    return 'private'
  }
  if (value === false) {
    return 'public'
  }
  return null
}

const toFile = (doc: DealConnectV2Document, dealId: string, dealDatasite?: string): DealConnectV2File => {
  const displayPath = decodeDisplayPath(doc.filePath, doc.source)
  const fileName = displayPath.split('/').pop() ?? displayPath
  const ext = fileName.split('.').pop()?.toLowerCase() ?? 'file'
  return {
    path: displayPath,
    name: fileName,
    filePath: doc.filePath,
    modifiedAt: doc.updatedAt,
    type: ext as FileType,
    dealSiteId: dealId,
    clientId: doc.clientId,
    source: doc.source,
    documentId: doc.documentId,
    privateDocument: toPrivateDocumentLabel(doc.privateDocument),
    datasite: doc.datasite || dealDatasite || '',
    downloadUrl: doc.downloadUrl || null,
  }
}

export const useDealConnectV2Store = defineStore('dealConnectV2', () => {
  const deals: Ref<DealConnectV2Deal[]> = ref([])
  const selectedDeal: Ref<DealConnectV2Deal | null> = ref(null)
  const documents: Ref<DealConnectV2File[]> = ref([])
  const loading: Ref<boolean> = ref(false)
  const documentsLoading: Ref<boolean> = ref(false)
  const error: Ref<string | null> = ref(null)
  const excludeDealsWithoutCompany: Ref<boolean> = ref(false)

  // Company-scoped state (for individual company deals page)
  const companyDocuments: Ref<DealConnectV2File[]> = ref([])
  const companyDocumentsLoading: Ref<boolean> = ref(false)
  const companyDealsError: Ref<string | null> = ref(null)

  const fetchDeals = async () => {
    loading.value = true
    error.value = null
    try {
      const req = await getReq()
      const res = await req.get('rac/dealconnect/deals')
      const body = res.data as DealConnectV2DealsResponse
      deals.value = body.deals
    } catch (e) {
      const err = e as Error
      console.error('[ERROR] Fetching dealconnect deals', err)
      error.value = 'Failed to load deals'
    } finally {
      loading.value = false
    }
  }

  const dealsLength = computed(() => deals.value.length)

  const fetchDocuments = async (dealId: string) => {
    documentsLoading.value = true
    try {
      error.value = null
      const req = await getReq()
      const res = await req.get('rac/dealconnect/documents', { params: { dealId } })
      const body = res.data as DealConnectV2DocumentsResponse
      const dealDatasite =
        deals.value.find((deal) => deal.id === dealId)?.datasite ??
        (selectedDeal.value?.id === dealId ? selectedDeal.value.datasite : undefined)
      documents.value = body.documents.map((doc) => toFile(doc, dealId, dealDatasite))
    } catch (e) {
      const err = e as Error
      console.error('[ERROR] Fetching dealconnect documents', err)
      error.value = 'Failed to load documents'
    } finally {
      documentsLoading.value = false
    }
  }

  const selectDeal = (deal: DealConnectV2Deal | null) => {
    selectedDeal.value = deal
    if (deal) {
      fetchDocuments(deal.id)
    } else {
      documents.value = []
    }
  }

  const getSignedDownloadUrl = async (path: string | null | undefined): Promise<string> => {
    if (!path) {
      throw new Error('Download URL is not available')
    }
    const req = await getReq()
    const res = await req.get(path)
    return res.data.url
  }

  const getManagedAccessDownloadUrl = async (dealId: string, clientId: string, filePath: string): Promise<string> => {
    const req = await getReq()
    const res = await req.get(`rac/clients/${clientId}/deals/${dealId}/${filePath}`)
    return res.request.responseURL
  }

  const getManagedAccessBulkDownloadUrls = async (clientId: string, companyId: string): Promise<string[]> => {
    const req = await getReq()
    const res = await req.get(`rac/clients/${clientId}/companies/${companyId}/deals/download`)
    const { downloadUrls } = res.data
    return downloadUrls.map((item: { downloadUrl: string }) => item.downloadUrl)
  }

  const getManagedAccessZipDownloadUrl = async (
    clientId: string,
    companyId: string,
    dealId: string,
  ): Promise<string> => {
    const req = await getReq()
    const res = await req.get(`rac/clients/${clientId}/companies/${companyId}/deals/download`)
    const { downloadUrls } = res.data
    const match = downloadUrls.find((item: { dealId: string; downloadUrl: string }) => item.dealId === dealId)
    if (!match) {
      throw new Error(`No managed access download URL found for deal ${dealId}`)
    }
    return match.downloadUrl
  }

  const dismissError = () => {
    error.value = null
  }

  const getDealsForCompany = (companyId: string): DealConnectV2Deal[] => {
    return deals.value.filter((deal) => deal.companyId?.includes(companyId))
  }

  const fetchDocumentsForCompany = async (companyId: string) => {
    companyDocumentsLoading.value = true
    companyDealsError.value = null
    try {
      if (!deals.value.length) {
        await fetchDeals()
      }
      const companyDeals = getDealsForCompany(companyId)
      const req = await getReq()
      const allDocs = await Promise.all(
        companyDeals.map(async (deal) => {
          const res = await req.get('rac/dealconnect/documents', { params: { dealId: deal.id } })
          const body = res.data as DealConnectV2DocumentsResponse
          return body.documents.map((doc) => toFile(doc, deal.id, deal.datasite))
        }),
      )
      companyDocuments.value = allDocs.flat()
    } catch (e) {
      const err = e as Error
      console.error('[ERROR] Fetching dealconnect v2 documents for company', err)
      companyDealsError.value = 'Failed to load deal documents'
    } finally {
      companyDocumentsLoading.value = false
    }
  }

  return {
    deals,
    selectedDeal,
    documents,
    loading,
    documentsLoading,
    dealsLength,
    error,
    fetchDeals,
    fetchDocuments,
    selectDeal,
    getSignedDownloadUrl,
    getManagedAccessDownloadUrl,
    getManagedAccessBulkDownloadUrls,
    getManagedAccessZipDownloadUrl,
    dismissError,
    excludeDealsWithoutCompany,
    companyDocuments,
    companyDocumentsLoading,
    companyDealsError,
    getDealsForCompany,
    fetchDocumentsForCompany,
  }
})
